import type { UseQueryResult } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import {
  ATTRIBUTION_GROUP_BY_LABELS,
  ATTRIBUTION_GROUP_BYS,
  META_GROUP_BYS,
  type AttributionGroupBy,
  type AttributionReport,
} from '@/api/attribution'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { CoverageIndicator } from '@/components/CoverageIndicator'
import { DataQualityNotice } from '@/components/DataQualityNotice'
import { ModelSelect } from '@/components/ModelSelect'
import { Card, LoadingBlock, SegmentedControl } from '@/design'
import { usePageFreshness } from '@/lib/freshness'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

import { TmtBadge } from './AttributionBadges'
import { AttributionError, NoMetaData, NoSales } from './AttributionStates'
import { AttributionTable } from './AttributionTable'
import {
  ATTRIBUTION_VIEW_LABELS,
  ATTRIBUTION_VIEWS,
  useAttributionQuery,
  useAttributionReport,
  useChannelReport,
  useGroupBy,
  useModel,
  useModelComparison,
  useView,
} from './attributionQuery'
import { ChannelTable } from './ChannelTable'
import { hasMetaObjects, isEmptyReport, unappliedFilters } from './definitions'
import { ModelComparison } from './ModelComparison'
import './attribution.css'

const VIEW_OPTIONS = ATTRIBUTION_VIEWS.map((value) => ({ value, label: ATTRIBUTION_VIEW_LABELS[value] }))

const LIST = new Intl.ListFormat('en-GB', { type: 'conjunction' })

/**
 * Marketing › Attribution (card 3d9c00e6; blueprint 05 §5.8, 06 §6.10, 14 D9): which
 * campaigns, sources and Meta ads the website's own sales are credited to, under a
 * chosen model — and all four models side by side. Every figure says it is TmT
 * first-party and under which model; Meta's spend, where there is any, says it is
 * Meta's; the unattributed share — Not tracked, apart from direct, since the coverage
 * card — is on screen whenever the API reports it; and the tracking baseline notice and
 * the share of orders tracked come from the report's `data_quality` block.
 */
export function AttributionPage({ route }: { route: RouteDef }) {
  const client = useApiClient()
  const { filters, setFilters } = useGlobalFilters()
  const [model, setModel] = useModel()
  const [groupBy, setGroupBy] = useGroupBy()
  const [view, setView] = useView()
  const query = useAttributionQuery(filters, model, groupBy)
  const report = useAttributionReport(query)
  // The comparison's three reads only start once the view is open: the table view is one request.
  const comparison = useModelComparison(query, view === 'compare')
  const channels = useChannelReport(query)
  const unapplied = unappliedFilters(filters)
  // The fact is rebuilt nightly and carries no build time, so the badge says live, not when.
  usePageFreshness(report.data ? { source: client.kind === 'http' ? 'live' : 'demo', lastUpdatedAt: null } : undefined)

  return (
    <SectionFrame
      route={route}
      actions={
        report.data ? (
          <span className="attr-source-group">
            <TmtBadge model={view === 'compare' ? 'all' : model} lookbackDays={report.data.lookbackDays} />
            <CoverageIndicator dataQuality={report.data.dataQuality} />
          </span>
        ) : null
      }
    >
      <div className="ci-stack">
        <div className="attr-controls">
          <ModelSelect value={model} onChange={setModel} />
          <label className="ci-field ci-field--inline">
            <span>Group by</span>
            <select value={groupBy} onChange={(event) => setGroupBy(event.target.value as AttributionGroupBy)}>
              {ATTRIBUTION_GROUP_BYS.map((option) => (
                <option key={option} value={option}>
                  {ATTRIBUTION_GROUP_BY_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
          <label className="attr-toggle">
            <input
              type="checkbox"
              checked={query.compare}
              onChange={(event) => setFilters({ compare: event.target.checked ? 'previous_period' : 'none' })}
            />
            <span>Compare with the previous period</span>
          </label>
          <SegmentedControl label="View" options={VIEW_OPTIONS} value={view} onChange={setView} />
        </div>

        <p role="note" className="attr-note">
          {unapplied.length > 0 ? (
            <>
              <strong>Production data, all websites.</strong> Attribution is counted on production only, across every
              website, so the filter bar&rsquo;s {LIST.format(unapplied)} {unapplied.length === 1 ? 'does' : 'do'} not
              apply here.{' '}
            </>
          ) : (
            'Production data, all websites. '
          )}
          Credit is rebuilt nightly, so today&rsquo;s sales may not be credited yet.
        </p>

        <DataQualityNotice dataQuality={report.data?.dataQuality} />

        {view === 'compare' ? (
          <ModelComparison comparison={comparison} groupBy={groupBy} />
        ) : (
          <ReportBody result={report} groupBy={groupBy} />
        )}

        <ChannelTable result={channels} />
      </div>
    </SectionFrame>
  )
}

function ReportBody({ result, groupBy }: { result: UseQueryResult<AttributionReport>; groupBy: AttributionGroupBy }) {
  if (result.isPending) return <LoadingBlock rows={6} label="Loading attributed sales" />
  if (!result.data) return <AttributionError error={result.error} />
  const report = result.data
  if (isEmptyReport(report)) return <NoSales from={report.start} to={report.end} />
  return (
    <Card
      title={`Attributed sales by ${ATTRIBUTION_GROUP_BY_LABELS[groupBy].toLowerCase()}`}
      subtitle={`${report.start} to ${report.end}${report.compared ? ` · compared with ${report.previousStart} to ${report.previousEnd}` : ''}`}
    >
      {META_GROUP_BYS.has(groupBy) && !hasMetaObjects(report) ? <NoMetaData /> : null}
      <AttributionTable report={report} />
    </Card>
  )
}
